/**
 * Money, to the cent.
 *
 * Amounts are plain numbers of dollars rather than integer cents, which is
 * fine for arithmetic this small but means every derived figure has to be
 * rounded deliberately -- otherwise a penny-a-square board starts showing
 * payouts like 0.30000000000000004.
 */

/** Rounded to whole cents. */
export function roundMoney(amount: number): number {
  if (!Number.isFinite(amount)) return 0;
  return Math.round(amount * 100) / 100;
}

/**
 * Rounded down to whole cents.
 *
 * For figures that have to divide into something a fixed number of times.
 * Rounding to nearest can land a fraction of a cent above the exact share,
 * which is enough for the last division to come up short -- a pot meant to
 * cover fourteen payouts covering only thirteen.
 */
export function floorMoney(amount: number): number {
  if (!Number.isFinite(amount)) return 0;
  return Math.floor(amount * 100) / 100;
}

/** Whether a figure is a usable amount of money: finite, and not sub-cent. */
export function isMoney(amount: unknown): amount is number {
  return typeof amount === 'number' && Number.isFinite(amount) && roundMoney(amount) === amount;
}

/**
 * Restates payouts against a new pot, keeping each one's share of it.
 *
 * Payouts are stored as amounts, not fractions, so changing the price per
 * square would otherwise leave them where they were: drop from $20 a square to
 * a penny and the board still promises the old figures, which it can no longer
 * come close to collecting. The admin had to rewrite every payout by hand
 * before the price would be accepted at all.
 *
 * Shares are preserved rather than the amounts, including any deliberate
 * shortfall -- an admin paying out only half the pot still pays out half.
 */
export function rescalePayouts(payouts: number[], fromPot: number, toPot: number): number[] {
  if (payouts.length === 0) return [];
  // No previous pot means no shares to preserve; there is nothing to scale.
  if (!(fromPot > 0)) return payouts.map(() => 0);

  const scaled = payouts.map((amount) => roundMoney((amount / fromPot) * toPot));

  // Rounding each share separately can drift a cent or two from what the whole
  // should be. The last payout absorbs it, as it does elsewhere.
  const promised = payouts.reduce((sum, n) => sum + n, 0);
  const target = roundMoney((promised / fromPot) * toPot);
  const actual = roundMoney(scaled.reduce((sum, n) => sum + n, 0));
  const last = scaled.length - 1;
  if (target !== actual) {
    scaled[last] = roundMoney(Math.max(0, scaled[last]! + (target - actual)));
  }

  // A board must never promise more than it can collect, whatever the
  // rounding did on the way here.
  const total = roundMoney(scaled.reduce((sum, n) => sum + n, 0));
  if (total > toPot) {
    scaled[last] = roundMoney(Math.max(0, scaled[last]! - (total - toPot)));
  }

  return scaled;
}
